import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  LayoutGrid,
  X,
  Plus,
  Paperclip,
  Check,
  Wrench,
  ChevronRight,
  PenSquare,
} from 'lucide-react';
import { SubmitButton } from './SubmitButton';
import { ModelSelector } from '../ModelSelection/ModelSelector';
import { useWebSocketStore } from '../../../../store/websocketStore';
import { LimitedFunctionalityBanner } from '../../../onboarding/components/LimitedFunctionalityBanner';
import { useChatStore, ContentBlock } from '../../store/chatStore';
import { useUserProfile } from '../../../../api/profile';
import { skillRegistry } from '../../../onboarding/registry/SkillRegistry';
import { useSkills, Skill } from '../../../onboarding/apis/skill-registry';
import { SkillConfig } from '../../../onboarding/types/onboarding';
import { useGetTenantIntegrations } from '../../../onboarding/apis/tenant-integrations';
import '../../../../styles/scrollbar.css';
import { FileAttachment } from '../ChatArea/FileAttachment';
import { FilePreview } from '../ChatArea/FilePreview';
import {
  processFile,
  FileUploadProcessingResult,
  validateFile,
  processFileWithUpload,
} from '../../../../utils/fileUtils';
import { ContextPreview } from './ContextPreview';
import { ChatInformationPreview } from './ChatInformationPreview';
import { ContextUsageProgress } from './ContextUsageProgress';
import { ContextUsageBanner } from './ContextUsageBanner';
import { InputButton } from './InputButton';
import { ACCEPT_TYPES } from '../../../../constants';
import { useFileUpload } from '../../../../api/file-upload';
import { useNavigate } from '@tanstack/react-router';
import { Tooltip } from '@plerionhq/design-system';
import { usePleriRenderOptional } from '../../context/usePleriRenderOptional';
import { isExtension } from '../../../../browser-extension/utils/extension-utils';
import { useListConversations } from '../../../../api/conversations';
import { useAvailableModels } from '../../../../api/models';
import { usePleriStatus } from '../../../onboarding/apis/pleri-status';

import { useTranslation } from 'react-i18next';

interface CenteredInputFormProps {
  isLoading?: boolean;
  onSubmit: (
    text: string,
    options?: {
      selectedToolGroups?: string[];
      enableReasoning?: boolean;
      attachedFiles?: ContentBlock[];
    },
  ) => void;
  dropUpMenu?: boolean;
  isSmallWindow?: boolean;
}

export const CenteredInputForm = ({
  isLoading,
  onSubmit,
  dropUpMenu = false,
  isSmallWindow = false,
}: CenteredInputFormProps) => {
  const { t } = useTranslation('pleri/chat');
  const { data: userProfile } = useUserProfile();
  const userOrgId = userProfile?.organizationId || '';
  const input = useChatStore((state) => state.inputPrompt);
  const setInput = useChatStore((state) => state.setInputPrompt);

  const isContentEnabled = useChatStore((state) => state.isContentEnabled);
  const setIsContentEnabled = useChatStore(
    (state) => state.setIsContentEnabled,
  );
  const selectedToolGroups = useChatStore((state) => state.selectedToolGroups);
  const setSelectedToolGroups = useChatStore(
    (state) => state.setSelectedToolGroups,
  );
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const attachedFiles = useChatStore((state) => state.attachedFiles);
  const setAttachedFiles = useChatStore((state) => state.setAttachedFiles);
  const uploadedFiles = useChatStore((state) => state.uploadedFiles);
  const setUploadedFiles = useChatStore((state) => state.setUploadedFiles);
  const wsStatus = useWebSocketStore((state) => state.status);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const userExplicitlyDisabled = useRef(false);
  const setActiveInputRef = useChatStore((state) => state.setActiveInputRef);
  // const selectedModelId = useChatStore((state) => state.selectedModelId);
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const stopStreaming = useChatStore((state) => state.stopStreaming);
  const isStopping = useChatStore(
    (state) =>
      state.conversations[state.activeConversationId ?? '']?.isStopping ?? false,
  );
  const activeFileUploadCount = useChatStore(
    (state) => state.activeFileUploadCount,
  );
  const hideIncludeContext = useChatStore(
    (state) =>
      state.conversations[state.activeConversationId ?? '']?.hideIncludeContext,
  );
  const hideToolsBadge = useChatStore(
    (state) =>
      state.conversations[state.activeConversationId ?? '']?.hideToolsBadge,
  );
  const contextUsage = useChatStore(
    (state) =>
      state.conversations[state.activeConversationId ?? '']?.contextUsage,
  );
  const { data: allConversationsData } = useListConversations({});
  const totalTokens =
    allConversationsData?.conversations?.find(
      (c) => c.conversationId === activeConversationId,
    )?.totalTokens ?? null;
  const { uploadFile } = useFileUpload();
  const incrementActiveUploads = useChatStore((s) => s.incrementActiveUploads);
  const decrementActiveUploads = useChatStore((s) => s.decrementActiveUploads);

  const { data: skills } = useSkills();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetTenantIntegrations();

  // Mirror the ModelSelector spinner: block submission until a model can be resolved
  const { isLoading: isLoadingModels } = useAvailableModels();
  const { isLoading: isPleriStatusLoading } = usePleriStatus();
  const isModelLoading = isLoadingModels || isPleriStatusLoading;

  const isPlerionEnabled =
    skills?.find((s) => s.skillType === 'plerion')?.enabled ?? false;

  const hasEnabledGithubIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'github' && integration.enabled,
    );

  const hasEnabledGitlabIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'gitlab' && integration.enabled,
    );

  const hasEnabledBitbucketIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'bitbucket' &&
        integration.enabled,
    );

  const visibleToolGroups = useMemo(() => {
    const uiSkills = skillRegistry.getAllSkills();

    const isAlwaysEnabledForOrg = (skillConfig: SkillConfig) => {
      if (!Array.isArray(skillConfig.alwaysEnabledForOrgs)) {
        return false;
      }

      return (
        skillConfig.alwaysEnabledForOrgs.includes('*') ||
        skillConfig.alwaysEnabledForOrgs.includes(userOrgId ?? '')
      );
    };

    const isGithubSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'github' &&
        hasEnabledGithubIntegration &&
        isPlerionEnabled
      );
    };

    const isGitlabSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'gitlab' &&
        hasEnabledGitlabIntegration &&
        isPlerionEnabled
      );
    };

    const isBitbucketSkillEnabled = (skillConfig: SkillConfig) => {
      return (
        skillConfig.id === 'bitbucket' &&
        hasEnabledBitbucketIntegration &&
        isPlerionEnabled
      );
    };

    const isSkillEnabled = (
      skill: SkillConfig,
      skillState: Skill | undefined,
    ) => {
      // Check if skill is always enabled for this organization
      if (isAlwaysEnabledForOrg(skill)) {
        return true;
      }

      // Check if GitHub skill has special conditions
      if (isGithubSkillEnabled(skill)) {
        return true;
      }

      // Check if GitLab skill has special conditions
      if (isGitlabSkillEnabled(skill)) {
        return true;
      }

      // Check if Bitbucket skill has special conditions
      if (isBitbucketSkillEnabled(skill)) {
        return true;
      }

      return skillState?.enabled ?? false;
    };

    const enabledToolGroups = uiSkills
      .map((skill) => {
        const skillState = skills?.find((s) => s.id === skill.id);
        if (isSkillEnabled(skill, skillState)) {
          return {
            id: skill.id,
            icon: skill.icon,
            label: skill.name,
          };
        }
        return null;
      })
      .filter((toolGroup) => toolGroup != null);

    return enabledToolGroups;
  }, [
    userOrgId,
    skills,
    hasEnabledGithubIntegration,
    hasEnabledGitlabIntegration,
    hasEnabledBitbucketIntegration,
    isPlerionEnabled,
  ]);

  const handleStop = useCallback(() => {
    if (activeConversationId) {
      stopStreaming(activeConversationId);
    }
  }, [activeConversationId, stopStreaming]);

  const navigate = useNavigate();
  const createConversation = useChatStore((state) => state.createConversation);
  const pleriRenderContext = usePleriRenderOptional();
  const renderType = pleriRenderContext?.renderType;

  const handleNewChat = useCallback(() => {
    const newId = createConversation();
    navigate({
      to: '/c/$conversationId',
      params: { conversationId: newId },
    });
  }, [createConversation, navigate]);

  // Register the textarea ref with the chat store
  useEffect(() => {
    setActiveInputRef(textareaRef as React.RefObject<HTMLTextAreaElement>); // TODO: Fix type
  }, [setActiveInputRef]);

  // Enable "Include page information" by default in Chrome extension
  // Re-enable it when it becomes false (e.g., after submission) unless user explicitly disabled it
  useEffect(() => {
    if (isExtension && !isContentEnabled && !userExplicitlyDisabled.current) {
      setIsContentEnabled(true);
    }
  }, [isContentEnabled, setIsContentEnabled]);

  // Focus textarea on initial load
  useEffect(() => {
    if (wsStatus === 'OPEN' && !isLoading) {
      textareaRef.current?.focus();
    }
  }, [wsStatus, isLoading]);

  // Add auto-growing behavior
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    // Use isSmallWindow prop to set responsive max height
    const maxHeight = 300;
    // If input is empty, set initial height to 52px, otherwise grow up to maxHeight
    const scrollHeight = textarea.scrollHeight;
    const calculatedHeight = !input.trim()
      ? 52
      : Math.min(scrollHeight, maxHeight);
    textarea.style.height = `${calculatedHeight}px`;
  }, [input, isSmallWindow]);

  // Add event listeners to prevent key events from propagating when input is focused
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInputFocused) {
        // Allow Enter and Escape keys to work normally
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
          return;
        }
        e.stopPropagation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isInputFocused) {
        // Allow Enter and Escape keys to work normally
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
          return;
        }
        e.stopPropagation();
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (isInputFocused) {
        // Allow Enter and Escape keys to work normally
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
          return;
        }
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('keypress', handleKeyPress, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('keypress', handleKeyPress, true);
    };
  }, [isInputFocused]);

  // Handler for cloud-uploaded files
  const handleFilesUploaded = useCallback(
    (results: FileUploadProcessingResult[]) => {
      setUploadedFiles([...uploadedFiles, ...results]);
    },
    [uploadedFiles, setUploadedFiles],
  );

  // Handle clicks outside menus to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        plusMenuRef.current &&
        !plusMenuRef.current.contains(event.target as Node)
      ) {
        setIsPlusMenuOpen(false);
        setIsToolsMenuOpen(false);
      }
      if (
        toolsMenuRef.current &&
        !toolsMenuRef.current.contains(event.target as Node)
      ) {
        setIsToolsMenuOpen(false);
      }
    };

    if (isPlusMenuOpen || isToolsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isPlusMenuOpen, isToolsMenuOpen]);

  // Handle file input change - uses same logic as FileAttachment
  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const fileArray = Array.from(event.target.files);

      // Compute totals from parent state
      const currentTotalFiles = attachedFiles.length + uploadedFiles.length;
      const currentTotalSize =
        attachedFiles.reduce((sum, f) => sum + (f.file?.size || 0), 0) +
        uploadedFiles.reduce(
          (sum, r) => sum + (r.uploadResult?.fileSize || 0),
          0,
        );

      // Check limits (same as FileAttachment)
      const maxTotalFiles = 5;
      const maxTotalSize = 10 * 1024 * 1024; // 10MB
      const remainingFiles = Math.max(0, maxTotalFiles - currentTotalFiles);
      const remainingSize = Math.max(0, maxTotalSize - currentTotalSize);

      if (remainingFiles === 0 || remainingSize === 0) {
        console.error('Attachment limit reached');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Apply remaining file count cap
      let filesAfterCountCap = fileArray;
      if (fileArray.length > remainingFiles) {
        filesAfterCountCap = fileArray.slice(0, remainingFiles);
      }

      // Reject files with the same name as already attached or uploaded (and duplicates within selection)
      const existingNames = new Set<string>();
      for (const f of attachedFiles) {
        if (f.file?.name) {
          existingNames.add(f.file.name);
        }
      }
      for (const r of uploadedFiles) {
        const name = r.uploadResult?.fileName;
        if (typeof name === 'string') {
          existingNames.add(name);
        }
      }
      const duplicateSkipped: string[] = [];
      const filesWithoutDuplicates = filesAfterCountCap.filter((file) => {
        if (existingNames.has(file.name)) {
          duplicateSkipped.push(file.name);
          return false;
        }
        existingNames.add(file.name);
        return true;
      });
      if (duplicateSkipped.length > 0) {
        console.warn(
          'Skipped duplicate file name(s):',
          duplicateSkipped.join(', '),
        );
      }
      if (filesWithoutDuplicates.length === 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Apply remaining size cap
      let usedSize = 0;
      const withinTotalLimit: File[] = [];
      for (const f of filesWithoutDuplicates) {
        if (usedSize + f.size <= remainingSize) {
          withinTotalLimit.push(f);
          usedSize += f.size;
        } else {
          break;
        }
      }

      // Validate files
      const validFiles: File[] = [];
      withinTotalLimit.forEach((file) => {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        }
      });

      if (validFiles.length === 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Process uploads
      const results: FileUploadProcessingResult[] = [];
      for (const file of validFiles) {
        incrementActiveUploads(1);
        try {
          const result = await processFileWithUpload(file, (f, onProgress) =>
            uploadFile(f, onProgress, {
              conversationId: activeConversationId ?? undefined,
            }),
          );
          results.push(result);
        } catch (error) {
          console.error('Upload error:', error);
        } finally {
          decrementActiveUploads(1);
        }
      }

      if (results.length > 0) {
        handleFilesUploaded(results);
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsPlusMenuOpen(false);
    },
    [
      attachedFiles,
      uploadedFiles,
      handleFilesUploaded,
      uploadFile,
      incrementActiveUploads,
      decrementActiveUploads,
    ],
  );

  // Trigger file selection
  const handleUploadFileClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setIsPlusMenuOpen(false);
  }, []);

  // Handle tool toggle
  const handleToolToggle = useCallback(
    (toolId: string) => {
      if (selectedToolGroups.includes(toolId)) {
        setSelectedToolGroups(selectedToolGroups.filter((id) => id !== toolId));
      } else {
        setSelectedToolGroups([...selectedToolGroups, toolId]);
      }
      setIsToolsMenuOpen(false);
      setIsPlusMenuOpen(false);
      textareaRef.current?.focus();
    },
    [selectedToolGroups, setSelectedToolGroups],
  );

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      const prev = attachedFiles;
      const removed = prev.find((f) => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      setAttachedFiles(prev.filter((f) => f.id !== fileId));
    },
    [attachedFiles, setAttachedFiles],
  );

  // Handler to remove uploaded files
  const handleRemoveUploadedFile = useCallback(
    (index: number) => {
      setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    },
    [uploadedFiles, setUploadedFiles],
  );

  const processAttachedFilesToContentBlocks = useCallback(async (): Promise<
    ContentBlock[]
  > => {
    const contentBlocks: ContentBlock[] = [];

    for (const attachedFile of attachedFiles) {
      try {
        const result = await processFile(attachedFile.file);
        if (result.success && result.contentBlock) {
          contentBlocks.push(result.contentBlock);
        } else {
          console.error(
            'Failed to process file:',
            attachedFile.file.name,
            result.error,
          );
        }
      } catch (error) {
        console.error('Error processing file:', attachedFile.file.name, error);
      }
    }

    return contentBlocks;
  }, [attachedFiles]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      // Process attached files into content blocks (for base64 files)
      let fileContentBlocks: ContentBlock[] = [];
      if (attachedFiles.length > 0) {
        try {
          fileContentBlocks = await processAttachedFilesToContentBlocks();
        } catch (error) {
          console.error('Error processing attached files:', error);
          return; // Don't submit if file processing fails
        }
      }

      // Add uploaded files content blocks (already processed)
      const uploadedContentBlocks: ContentBlock[] = uploadedFiles
        .filter((result) => result.success && result.contentBlock)
        .map((result) => result.contentBlock!);

      const allFileContentBlocks = [
        ...fileContentBlocks,
        ...uploadedContentBlocks,
      ];

      // Call the parent's onSubmit with files
      if (input.trim() || allFileContentBlocks.length > 0) {
        onSubmit(input, {
          selectedToolGroups,
          attachedFiles: allFileContentBlocks,
        });

        // Clear input after successful submission
        setInput('');

        // Clear attached files after successful submission
        // Clean up previews
        attachedFiles.forEach((file) => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
        setAttachedFiles([]);
        setUploadedFiles([]);
      }
    },
    [
      attachedFiles,
      uploadedFiles,
      input,
      processAttachedFilesToContentBlocks,
      onSubmit,
      selectedToolGroups,
      setInput,
      setAttachedFiles,
      setUploadedFiles,
    ],
  );

  // Update: Only show context toggles if there is an active conversation and hideIncludeContext is not true
  const showIncludeContext = !!activeConversationId && !hideIncludeContext;
  // Update: Only show tools badge if there is an active conversation and hideToolsBadge is not true
  const showToolsBadge = !!activeConversationId && !hideToolsBadge;

  return (
    <div className="relative w-full">
      <LimitedFunctionalityBanner />
      <ContextPreview />
      <ChatInformationPreview />
      {/* Context Usage Banner */}
      <ContextUsageBanner contextUsage={contextUsage} className="mb-4" />
      {/* Selected Options Display - Minimal chips */}
      {(showIncludeContext && isContentEnabled) ||
      (showToolsBadge && selectedToolGroups.length > 0) ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {/* Page Information Included Chip */}
          {showIncludeContext && isContentEnabled && (
            <button
              type="button"
              onClick={() => {
                setIsContentEnabled(false);
                userExplicitlyDisabled.current = true;
                textareaRef.current?.focus();
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-950"
            >
              <LayoutGrid className="h-3.5 w-3.5 text-blue-600" />
              <span>{t('includedPageInformation')}</span>
              <X className="h-3 w-3 text-blue-600" />
            </button>
          )}

          {/* Selected Tools Chips */}
          {showToolsBadge &&
            selectedToolGroups.length > 0 &&
            visibleToolGroups
              .filter((tool) => selectedToolGroups.includes(tool.id))
              .map((tool) => (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => {
                    setSelectedToolGroups(
                      selectedToolGroups.filter((id) => id !== tool.id),
                    );
                    textareaRef.current?.focus();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#FF732E]/30 bg-[#FF732E]/10 px-2.5 py-1 text-xs font-medium text-[#FF732E] transition-colors hover:border-[#FF732E]/50 hover:bg-[#FF732E]/20"
                >
                  <div className="h-3.5 w-3.5">{tool.icon}</div>
                  <span>{tool.label}</span>
                  <X className="h-3 w-3 text-[#FF732E]" />
                </button>
              ))}
        </div>
      ) : null}
      {/* File Preview with immediate image previews */}
      <FilePreview
        attachedFiles={attachedFiles}
        uploadedFiles={uploadedFiles}
        onRemoveFile={handleRemoveFile}
        onRemoveUploadedFile={handleRemoveUploadedFile}
      />
      <form
        onSubmit={handleSubmit}
        className="mb-2 cursor-text outline-none"
        onClick={() => {
          textareaRef.current?.focus();
        }}
      >
        <div className="relative rounded-2xl border border-gray-200 bg-gray-50/30 shadow-sm transition-all focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-md dark:border-gray-700 dark:bg-gray-800/30 dark:focus-within:border-gray-600 dark:focus-within:bg-gray-800">
          <textarea
            ref={textareaRef}
            value={input}
            data-testid="pleri-input-textarea"
            autoFocus={true}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (activeFileUploadCount === 0) {
                  handleSubmit();
                }
              }
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            placeholder={
              wsStatus === 'OPEN' && !isModelLoading
                ? t('messagePleri')
                : t('connecting')
            }
            disabled={
              wsStatus !== 'OPEN' || isLoading || isStopping || isModelLoading
            }
            rows={1}
            className="custom-scrollbar w-full resize-none border-0 bg-transparent px-5 pt-5 pb-2 text-gray-900 outline-none placeholder:text-gray-500 focus:ring-0 disabled:opacity-50 dark:text-gray-100 dark:placeholder:text-gray-400"
            style={{
              boxShadow: 'none',
            }}
          />

          {/* Hidden file input for plus menu */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_TYPES}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={wsStatus !== 'OPEN' || isLoading}
          />

          {/* Keep FileAttachment fully rendered for drag-and-drop overlay */}
          {/* Position it off-screen but keep it fully visible so the fixed overlay can render */}
          <div
            className="absolute"
            style={{
              left: '-9999px',
              top: '-9999px',
              width: '1px',
              height: '1px',
            }}
          >
            <FileAttachment
              onFilesUploaded={handleFilesUploaded}
              disabled={wsStatus !== 'OPEN' || isLoading}
              maxTotalFiles={5}
              maxTotalSize={10 * 1024 * 1024}
              attachedFiles={attachedFiles}
              uploadedFiles={uploadedFiles}
              conversationId={activeConversationId ?? undefined}
            />
          </div>

          {/* Render upload progress indicator separately so it's visible */}
          {activeFileUploadCount > 0 && (
            <div className="absolute -top-10 left-1/2 z-50 -translate-x-1/2">
              <div className="rounded bg-black px-3 py-1.5 text-xs whitespace-nowrap text-white shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                  <span>{t('uploading')}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 px-3 pt-1.5 pb-3">
            <div className="flex items-center gap-3">
              {/* Plus icon menu */}
              <div className="relative" ref={plusMenuRef}>
                <InputButton
                  icon={<Plus className="h-5 w-5" />}
                  onClick={() => setIsPlusMenuOpen(!isPlusMenuOpen)}
                  disabled={wsStatus !== 'OPEN' || isLoading}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
                />

                {isPlusMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsPlusMenuOpen(false)}
                    />
                    <div
                      className={`absolute z-20 min-w-[180px] rounded-xl border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${
                        dropUpMenu ? 'bottom-full mb-2' : 'top-full mt-2'
                      }`}
                    >
                      {/* Upload file option */}
                      <button
                        type="button"
                        onClick={handleUploadFileClick}
                        disabled={wsStatus !== 'OPEN' || isLoading}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <Paperclip className="h-4 w-4" />
                        <span>{t('uploadFile')}</span>
                      </button>

                      {/* Include page information option */}
                      {showIncludeContext && (
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !isContentEnabled;
                            setIsContentEnabled(newValue);
                            if (!newValue) {
                              userExplicitlyDisabled.current = true;
                            } else {
                              userExplicitlyDisabled.current = false;
                            }
                            setIsPlusMenuOpen(false);
                            textareaRef.current?.focus();
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <LayoutGrid className="h-4 w-4" />
                          <span
                            className={
                              isContentEnabled
                                ? 'text-blue-700'
                                : 'text-gray-700 dark:text-gray-300'
                            }
                          >
                            {t('includePageInformation')}
                          </span>
                          {isContentEnabled && (
                            <Check className="ml-auto h-4 w-4 text-blue-600" />
                          )}
                        </button>
                      )}

                      {/* Tools option - opens nested menu */}
                      {visibleToolGroups.length > 0 && showToolsBadge && (
                        <div className="relative" ref={toolsMenuRef}>
                          <button
                            type="button"
                            onClick={() => setIsToolsMenuOpen(!isToolsMenuOpen)}
                            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <Wrench className="h-4 w-4" />
                            <span>{t('tools')}</span>
                            {selectedToolGroups.length > 0 && (
                              <div className="ml-auto flex items-center gap-1.5">
                                <div className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF732E] text-xs font-medium text-white">
                                  {selectedToolGroups.length}
                                </div>
                                <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                            {selectedToolGroups.length === 0 && (
                              <ChevronRight className="ml-auto h-4 w-4 text-gray-400 dark:text-gray-500" />
                            )}
                          </button>

                          {isToolsMenuOpen && (
                            <div
                              className={`absolute left-full z-30 ml-2 min-w-[200px] rounded-xl border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${
                                dropUpMenu ? 'bottom-0' : 'top-0'
                              }`}
                            >
                              {visibleToolGroups.map((tool) => (
                                <button
                                  key={tool.id}
                                  type="button"
                                  onClick={() => handleToolToggle(tool.id)}
                                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                                    selectedToolGroups.includes(tool.id)
                                      ? 'bg-[#FF732E]/10 text-[#FF732E] hover:bg-[#FF732E]/20'
                                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  <div className="h-4 w-4">{tool.icon}</div>
                                  <span>{tool.label}</span>
                                  {selectedToolGroups.includes(tool.id) && (
                                    <Check className="ml-auto h-4 w-4 text-[#FF732E]" />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Context Usage Progress and Model Selector Group */}
            <div className="ml-auto flex items-center gap-2">
              {contextUsage && (
                <div className="flex-shrink-0">
                  <ContextUsageProgress
                    contextUsage={contextUsage}
                    totalTokens={totalTokens}
                  />
                </div>
              )}
              <ModelSelector condensed={true} className="flex-shrink-0" />
            </div>
            {/* New Chat Button - Only show in extension */}
            {renderType === 'extension' && (
              <Tooltip label={t('startANewChat')}>
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="group flex h-10 w-10 items-center justify-center rounded-full text-gray-900 transition-all duration-150 hover:bg-gray-200 hover:shadow-sm disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:shadow-none dark:text-gray-100 dark:hover:bg-gray-600"
                  title={t('startANewChat')}
                  aria-label={t('startANewChat')}
                  disabled={isLoading || activeFileUploadCount > 0}
                >
                  <PenSquare className="h-5 w-5 stroke-[1.8] text-gray-900 transition-colors duration-150 group-hover:text-gray-900 dark:text-gray-100" />
                </button>
              </Tooltip>
            )}
            {isStopping ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('stopping')}
                </span>
                <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 animate-spin rounded-full border-2 border-gray-400/20 border-t-gray-400 dark:border-gray-500/20 dark:border-t-gray-400"></div>
                  <div className="relative flex h-3 w-3 items-center justify-center">
                    <div className="h-2 w-2 bg-gray-500 dark:bg-gray-400"></div>
                  </div>
                </div>
              </div>
            ) : isLoading ? (
              <button
                type="button"
                onClick={handleStop}
                className="relative flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-white transition-colors hover:bg-red-600 disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-400"
                title={t('stopGenerating')}
              >
                <div className="absolute inset-0 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                <div className="relative flex h-3 w-3 items-center justify-center">
                  <div className="h-2 w-2 bg-white"></div>
                </div>
              </button>
            ) : (
              <SubmitButton
                onClick={handleSubmit}
                disabled={
                  wsStatus !== 'OPEN' ||
                  isLoading ||
                  isModelLoading ||
                  activeFileUploadCount > 0
                }
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
